import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { Panel } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ScenarioList({ gameId, reload, onLoad, onSave, canSave }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/rotation/scenarios", { params: { game_id: gameId } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [gameId, reload, tick]);
  const activate = async (s) => {
    try {
      await api.post(`/rotation/scenarios/${s.scenario_id}/activate`, null, { params: { active: !s.active } });
      toast.success(s.active ? "Scenario removed from slate totals" : "Scenario applied to slate team totals");
      setTick((n) => n + 1);
    } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/rotation/scenarios/${id}`); setTick((n) => n + 1); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Panel className="p-4" data-testid="rotation-scenarios">
      <h3 className="font-display text-xl font-bold uppercase tracking-wide mb-3">Saved scenarios</h3>
      <div className="flex gap-2 mb-3">
        <Input data-testid="rotation-note-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-900/70" />
        <Button data-testid="rotation-save-btn" disabled={!canSave} onClick={async () => { await onSave(note); setNote(""); }}><Save className="h-4 w-4" /> Save</Button>
      </div>
      {!items.length && <p className="text-xs text-slate-500">No scenarios for this game yet.</p>}
      <ul className="space-y-2">
        {items.map((s) => (
          <li key={s.scenario_id} data-testid={`rotation-scenario-${s.scenario_id}`} className="rounded-md border border-slate-800 p-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="flex-1 min-w-[150px] text-left hover:text-blue-300" onClick={() => onLoad(s)} data-testid={`rotation-scenario-load-${s.scenario_id}`}>
                <span className="font-bold text-slate-100">{s.team_code}</span> · {s.out_ids.length} OUT · {Object.keys(s.fixed_minutes || {}).length} fixed ·{" "}
                <span className="font-mono-num">{s.result.after.totals.pts.toFixed(1)} PTS</span>
                <span className="block text-slate-500">{dateTime(s.created_at)} · {s.created_by}{s.note ? ` · ${s.note}` : ""}</span>
              </button>
              <button type="button" data-testid={`rotation-scenario-activate-${s.scenario_id}`} onClick={() => activate(s)}
                className={`whitespace-nowrap rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${s.active ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>
                {s.active ? "Active on slate" : "Apply to slate"}
              </button>
              <button type="button" aria-label="Delete scenario" data-testid={`rotation-scenario-delete-${s.scenario_id}`} onClick={() => remove(s.scenario_id)} className="shrink-0 text-slate-500 hover:text-rose-300">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
